<template>
  <div class="card">
    <Toast />
    <div class="title">Daftar Shift</div>

    <Toolbar class="p-mb-4">
      <template slot="left">
        <Button
          label="Tambah"
          icon="pi pi-plus"
          class="p-button-success"
          @click="create_dialog = true"
        />
      </template>
      <template slot="right"> </template>
    </Toolbar>

    <DataTable
      :value="shifts"
      :lazy="true"
      :paginator="showPagination"
      :rows="limit"
      :totalRecords="total_records"
      :first="first"
      :loading="loading.main"
      @page="onPage"
    >
      <template #header>
        <div class="p-grid p-fluid p-mb-2">
          <div class="p-col-12 p-md-4 p-lg-4">
            <InputText
              id="filter-name"
              v-model="filter.name"
              placeholder="Nama Kasir"
              @input="handleFilterInput"
            />
          </div>
          <div class="p-col-12 p-md-4 p-lg-4">
            <InputText
              id="filter-audit-date"
              v-model="filter.audit_date"
              type="date"
              @change="handleFilterInput"
            />
          </div>
          <div class="p-col-12 p-md-4 p-lg-4">
            <Dropdown
              id="filter-status"
              v-model="filter.status"
              :options="status"
              placeholder="Status"
              :showClear="true"
              @change="handleFilterInput"
            />
          </div>
        </div>

        <div class="p-grid">
          <div class="p-col-12 p-md-4 p-lg-4">
            <Button
              label="Cari"
              icon="pi pi-search"
              class="p-button-info"
              @click="getShifts()"
            />
            <Button
              label="Reset"
              icon="pi pi-refresh"
              class="p-button-secondary p-ml-2"
              @click="resetFilters()"
            />
          </div>
        </div>
      </template>

      <template #empty>Tidak ada data shift</template>
      <template #loading>Mengambil data shift. Mohon tunggu...</template>

      <Column field="name" header="Nama">
        <template #body="slotProps">
          {{ slotProps.data.name }}
        </template>
      </Column>
      <Column field="audit_date" header="Tanggal Audit">
        <template #body="slotProps">
          {{ slotProps.data.audit_date }}
        </template>
      </Column>
      <Column header="Mulai">
        <template #body="slotProps">
          <span
            v-if="
              slotProps.data.opened_at &&
              slotProps.data.opened_at !== '0001-01-01 00:00:00' &&
              moment(slotProps.data.opened_at).isValid()
            "
          >
            {{
              moment(moment.utc(slotProps.data.opened_at).toDate())
                .local()
                .format('YYYY-MM-DD HH:mm:ss')
            }}
          </span>
        </template>
      </Column>
      <Column header="Selesai">
        <template #body="slotProps">
          <span
            v-if="
              slotProps.data.closed_at &&
              slotProps.data.closed_at !== '0001-01-01 00:00:00' &&
              moment(slotProps.data.closed_at).isValid()
            "
          >
            {{
              moment(moment.utc(slotProps.data.closed_at).toDate())
                .local()
                .format('YYYY-MM-DD HH:mm:ss')
            }}
          </span>
        </template>
      </Column>
      <Column header="Saldo Awal">
        <template #body="slotProps">
          {{ slotProps.data.opening_balance | currency }}
        </template>
      </Column>
      <Column header="Saldo Akhir">
        <template #body="slotProps">
          {{ slotProps.data.closing_balance | currency }}
        </template>
      </Column>
      <Column field="status" header="Status">
        <template #body="slotProps">
          <span
            v-if="slotProps.data.status === 'CLOSE'"
            class="p-text-bold"
            style="color: black"
            >{{ slotProps.data.status }}</span
          >
          <span
            v-else-if="slotProps.data.status === 'OPEN'"
            class="p-text-bold"
            style="color: green"
            >{{ slotProps.data.status }}</span
          >
          <span
            v-else-if="slotProps.data.status === 'PENDING'"
            class="p-text-bold"
            style="color: orange"
            >{{ slotProps.data.status }}</span
          >
          <span
            v-else-if="slotProps.data.status === 'CANCEL'"
            class="p-text-bold"
            style="color: grey"
            >{{ slotProps.data.status }}</span
          >
        </template>
      </Column>
      <Column headerStyle="width: 5em" header="Aksi">
        <template #body="slotProps">
          <Button
            v-if="slotProps.data.status == 'PENDING'"
            icon="pi pi-times"
            class="p-button-rounded p-button-warning"
            @click="confirmCancel(slotProps.data)"
            :disabled="loading.cancel"
            v-tooltip.top="'Batalkan Shift'"
          ></Button>
        </template>
      </Column>
    </DataTable>

    <Dialog
      :visible.sync="cancel_dialog"
      :style="{ width: '450px' }"
      header="Konfirmasi Pembatalan"
      :modal="true"
      class="p-fluid"
    >
      <div class="confirmation-content">
        Apakah kamu yakin ingin membatalkan shift <b>{{ cancel_data.name }}</b
        >?
      </div>

      <template #footer>
        <Button
          label="No"
          icon="pi pi-times"
          class="p-button-text"
          @click="cancel_dialog = false"
        />
        <Button
          label="Yes"
          icon="pi pi-check"
          class="p-button-text"
          @click="cancelShift()"
          :disabled="loading.cancel"
        >
          <span v-if="loading.cancel">
            <i class="pi pi-spin pi-spinner p-mr-2"></i>
          </span>
        </Button>
      </template>
    </Dialog>

    <Dialog
      :visible.sync="create_dialog"
      :style="{ width: '450px' }"
      header="Tambah Shift"
      :modal="true"
      class="p-fluid"
    >
      <div class="p-field">
        <label for="cashier-select">Kasir</label>
        <AutoComplete
          id="cashier-select"
          placeholder="Kasir"
          :dropdown="true"
          :multiple="false"
          v-model="cashier_selected"
          :suggestions="cashier_filtered"
          @complete="searchCashier($event)"
          @input="is_error.cashier_id = false"
          field="name"
        />
        <small class="p-error" v-if="is_error.cashier_id">{{
          error_msg.cashier_id
        }}</small>
      </div>
      <div class="p-field">
        <label for="opening-balance">Saldo Awal</label>
        <InputNumber
          id="opening-balance"
          locale="id-ID"
          v-model="create_data.opening_balance"
          placeholder="0"
          @keyup.prevent="is_error.opening_balance = false"
        />
        <small class="p-error" v-if="is_error.opening_balance">{{
          error_msg.opening_balance
        }}</small>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          class="p-button-outlined"
          @click="handleCloseCreateDialog"
        />
        <Button
          type="button"
          label="Save"
          class="btn btn-primary"
          @click="createShift()"
          :disabled="loading.create"
        >
          <span v-if="loading.create">
            <i class="pi pi-spin pi-spinner p-mr-2"></i>
          </span>
          Save
        </Button>
      </template>
    </Dialog>
  </div>
</template>

<script>
var moment = require('moment')

export default {
  data() {
    return {
      moment: moment,

      cancel_dialog: false,
      create_dialog: false,

      loading: {
        main: false,
        create: false,
        cancel: false,
        cashiers: false
      },

      abortControllers: {
        shifts: null,
        cashiers: null
      },

      cashiers: [],
      cashier_filtered: [],
      cashier_selected: null,

      shifts: [],
      filter: {
        name: '',
        audit_date: '',
        status: ''
      },
      status: ['PENDING', 'OPEN', 'CLOSE', 'CANCEL'],

      create_data: {
        cashier_id: 0,
        opening_balance: null
      },
      cancel_data: {},

      is_error: {
        cashier_id: false,
        opening_balance: false
      },
      error_msg: {
        cashier_id: '',
        opening_balance: ''
      },

      limit: 20,
      offset: 0,
      first: 0,
      order_by: '-audit_date,-id',
      total_records: 0,

      $_searchDebounced: null
    }
  },
  computed: {
    showPagination() {
      return this.total_records > this.limit
    }
  },
  mounted() {
    this.initDebouncedSearch()
    this.getShifts()
    this.getCashiers()
  },
  beforeDestroy() {
    // Cancel all pending requests
    Object.keys(this.abortControllers).forEach(key => {
      if (this.abortControllers[key]) {
        this.abortControllers[key].abort()
        this.abortControllers[key] = null
      }
    })
  },
  methods: {
    initDebouncedSearch() {
      this.$_searchDebounced = this.debounce(() => {
        this.first = 0
        this.offset = 0
        this.getShifts()
      }, 500)
    },

    debounce(func, wait) {
      let timeout
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout)
          func(...args)
        }
        clearTimeout(timeout)
        timeout = setTimeout(later, wait)
      }
    },

    handleFilterInput() {
      if (this.$_searchDebounced) {
        this.$_searchDebounced()
      }
    },

    onPage(event) {
      this.first = event.first
      this.getShifts(event)
    },

    async getShifts(event = null) {
      // Cancel previous request if exists
      if (this.abortControllers.shifts) {
        this.abortControllers.shifts.abort()
      }
      this.abortControllers.shifts = new AbortController()

      // Calculate offset from pagination event
      if (event) {
        this.first = event.first
        this.offset = event.first
      } else {
        this.first = 0
        this.offset = 0
      }

      // Build query parameters using URLSearchParams
      const params = new URLSearchParams({
        limit: this.limit,
        offset: this.offset,
        order_by: this.order_by
      })

      if (this.filter.name) {
        params.append('name', this.filter.name)
      }

      if (this.filter.audit_date) {
        const audit_date = moment(this.filter.audit_date).format('YYYY-MM-DD')
        params.append('audit_date', audit_date)
      }

      if (this.filter.status) {
        params.append('status', this.filter.status)
      }

      const url = `${process.env.VUE_APP_API_URL}/shifts?${params.toString()}`
      this.loading.main = true

      try {
        const response = await this.$http.get(url, {
          signal: this.abortControllers.shifts.signal
        })

        if (response.status === 200) {
          this.shifts = response.data.data.shifts || []
          this.total_records = response.data.data.total || 0
        } else {
          this.showToast('error', 'Error', 'Gagal mengambil data shift')
        }
      } catch (error) {
        // Don't show error if request was aborted
        if (error.name === 'AbortError') {
          return
        }
        console.error('Error fetching shifts:', error)
        const errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Terjadi kesalahan saat mengambil data shift'
        this.showToast('error', 'Error', errorMessage)
        this.shifts = []
        this.total_records = 0
      } finally {
        this.loading.main = false
      }
    },

    resetFilters() {
      this.filter.name = ''
      this.filter.audit_date = ''
      this.filter.status = ''
      this.first = 0
      this.offset = 0
      this.getShifts()
    },

    async getCashiers() {
      // Cancel previous request if exists
      if (this.abortControllers.cashiers) {
        this.abortControllers.cashiers.abort()
      }
      this.abortControllers.cashiers = new AbortController()

      this.loading.cashiers = true

      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/employees?role=CASHIER`,
          {
            signal: this.abortControllers.cashiers.signal
          }
        )

        if (response.status === 200) {
          this.cashiers = response.data.data.employees || []
        } else {
          this.showToast('error', 'Error', 'Gagal mengambil data kasir')
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return
        }
        console.error('Error fetching cashiers:', error)
        const errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          'Terjadi kesalahan saat mengambil data kasir'
        this.showToast('error', 'Error', errorMessage)
        this.cashiers = []
      } finally {
        this.loading.cashiers = false
      }
    },

    searchCashier(event) {
      this.cashier_filtered = this.cashiers.filter(item => {
        return item.name.toLowerCase().startsWith(event.query.toLowerCase())
      })
    },

    createShift() {
      // Reset error states
      this.is_error.cashier_id = false
      this.is_error.opening_balance = false
      this.error_msg.cashier_id = ''
      this.error_msg.opening_balance = ''

      // Set cashier_id if selected
      if (this.cashier_selected) {
        this.create_data.cashier_id = this.cashier_selected.id
      }

      // Validate cashier
      if (!this.create_data.cashier_id || this.create_data.cashier_id === 0) {
        this.is_error.cashier_id = true
        this.error_msg.cashier_id = 'Pilih kasir'
      }

      // Validate opening balance
      if (
        !this.create_data.opening_balance ||
        this.create_data.opening_balance === null
      ) {
        this.is_error.opening_balance = true
        this.error_msg.opening_balance = 'Isi saldo awal'
      }

      // If validation fails, stop here
      if (this.is_error.cashier_id || this.is_error.opening_balance) {
        return
      }

      // Proceed with API call
      this.loading.create = true

      this.$http
        .post(`${process.env.VUE_APP_API_URL}/shifts`, this.create_data)
        .then(response => {
          this.loading.create = false

          if (response.status === 200) {
            this.create_dialog = false
            this.resetCreateData()
            this.getShifts()

            this.showToast('success', 'Berhasil', 'Berhasil membuat shift')
          } else {
            const errorMessage =
              (response.data && response.data.message) || 'Gagal membuat shift'
            this.showToast('error', 'Error', errorMessage)
          }
        })
        .catch(error => {
          this.loading.create = false
          console.error('Error creating shift:', error)
          const errorMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            'Terjadi kesalahan saat membuat shift'
          this.showToast('error', 'Error', errorMessage)
        })
    },

    resetCreateData() {
      this.cashier_selected = null
      this.create_data = {
        cashier_id: 0,
        opening_balance: null
      }
      this.is_error.cashier_id = false
      this.is_error.opening_balance = false
      this.error_msg.cashier_id = ''
      this.error_msg.opening_balance = ''
    },

    handleCloseCreateDialog() {
      this.create_dialog = false
      this.resetCreateData()
    },

    confirmCancel(shift) {
      this.cancel_data = shift
      this.cancel_dialog = true
    },

    cancelShift() {
      this.loading.cancel = true

      this.$http
        .post(
          `${process.env.VUE_APP_API_URL}/shifts/${this.cancel_data.id}/cancel`
        )
        .then(response => {
          this.loading.cancel = false
          this.cancel_dialog = false

          if (response.status === 200) {
            this.getShifts()
            this.showToast('success', 'Berhasil', 'Berhasil membatalkan shift')
          } else {
            const errorMessage =
              (response.data && response.data.message) ||
              'Gagal membatalkan shift'
            this.showToast('error', 'Error', errorMessage)
          }
        })
        .catch(error => {
          this.loading.cancel = false
          console.error('Error canceling shift:', error)
          const errorMessage =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            'Terjadi kesalahan saat membatalkan shift'
          this.showToast('error', 'Error', errorMessage)
        })
    },

    showToast(severity, summary, detail) {
      this.$toast.add({
        severity: severity,
        summary: summary,
        detail: detail,
        life: 3000
      })
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep(.p-datatable table) {
  border-collapse: collapse;
}

::v-deep(.p-datatable th),
::v-deep(.p-datatable td) {
  border: 1px solid #ccc !important;
  padding: 8px;
}
</style>
